/**
 * XR8Pipeline - Custom 8th Wall pipeline module for StorySplat.
 *
 * Hooks StorySplat lifecycle and diagnostics into the 8th Wall PlayCanvas wrapper.
 * The pipeline module:
 *   - On attach/start: sets AR mode and disables camera controls
 *   - On update: reports reality/tracking status from XR8
 *   - On detach/stop: restores controls through the viewer teardown path
 */

import * as pc from 'playcanvas';
import type { XR8PipelineModule } from './XR8Types';
import { arDebug, arError, arInfo } from './ARLogger';

export interface XR8PipelineConfig {
    /** PlayCanvas application instance */
    app: pc.Application;
    /** PlayCanvas camera entity */
    camera: pc.Entity;
    /** Scene root entity (content to be placed in AR) */
    sceneRoot: pc.Entity;
    /** Callback to disable camera controls when entering AR */
    onDisableControls: () => void;
    /** Callback to restore camera controls when exiting AR */
    onRestoreControls: () => void;
    /** Callback when AR session starts */
    onARStart?: () => void;
    /** Callback when AR session ends */
    onAREnd?: () => void;
    /** Callback for tracking status changes */
    onTrackingStatus?: (status: string, reason?: string) => void;
    /** Callback for runtime AR errors */
    onException?: (error: Error) => void;
    /**
     * Camera feed status ('requesting', 'hasStream', 'hasVideo', 'failed'). On 'failed' the camera
     * could not open (permission denied, no camera, or blocked in an iframe) and the engine does
     * not attach or report an exception, so the app must tell the user.
     * https://8thwall.org/docs/api/engine/camerapipelinemodule/oncamerastatuschange
     */
    onCameraStatus?: (status: string) => void;
}

/**
 * Create the StorySplat AR pipeline module for 8th Wall.
 *
 * This module bridges 8th Wall's camera pose data to the PlayCanvas camera,
 * enabling AR rendering where splats appear over the real-world camera feed.
 */
export function createStorySplatPipeline(config: XR8PipelineConfig): XR8PipelineModule {
    const { app, camera, onDisableControls, onRestoreControls, onARStart, onAREnd, onTrackingStatus, onException, onCameraStatus } = config;

    let attached = false;
    let frameCount = 0;
    let loggedFirstRealityFrame = false;
    let lastTrackingStatus = '';
    let lastTrackingReason = '';

    const enter = (source: string, args?: Record<string, unknown>): void => {
        if (attached) {
            arDebug('StorySplat XR8 pipeline enter ignored because it is already attached', { source });
            return;
        }

        attached = true;
        frameCount = 0;
        loggedFirstRealityFrame = false;
        lastTrackingStatus = '';
        lastTrackingReason = '';

        arInfo('StorySplat XR8 pipeline attached', {
            source,
            canvas: args?.canvas instanceof HTMLCanvasElement
                ? { width: args.canvas.width, height: args.canvas.height, clientWidth: args.canvas.clientWidth, clientHeight: args.canvas.clientHeight }
                : undefined,
            appAutoRender: app.autoRender,
            cameraPosition: camera.getPosition(),
        });

        onDisableControls();
        onARStart?.();
    };

    const leave = (source: string): void => {
        if (!attached) {
            arDebug('StorySplat XR8 pipeline leave ignored because it is already detached', { source });
            return;
        }

        attached = false;
        arInfo('StorySplat XR8 pipeline detached', {
            source,
            frames: frameCount,
            lastTrackingStatus,
            lastTrackingReason,
        });

        onRestoreControls();
        onAREnd?.();
    };

    return {
        name: 'storysplat-ar',

        onAttach(args) {
            enter('onAttach', args as unknown as Record<string, unknown>);
        },

        onStart({ canvas, GLctx }) {
            enter('onStart', { canvas, hasGLContext: !!GLctx });
        },

        onUpdate({ processCpuResult }) {
            frameCount += 1;
            const reality = processCpuResult?.reality;
            if (!reality) {
                if (frameCount <= 5) {
                    arDebug('XR8 update without reality payload', { frameCount });
                }
                return;
            }

            if (!loggedFirstRealityFrame) {
                loggedFirstRealityFrame = true;
                arInfo('XR8 reality frame received', {
                    frameCount,
                    hasPosition: !!reality.position,
                    hasRotation: !!reality.rotation,
                    hasIntrinsics: !!reality.intrinsics,
                    hasRealityTexture: !!reality.realityTexture,
                    trackingStatus: reality.trackingStatus,
                    trackingReason: reality.trackingReason,
                });
            }

            // Report tracking status
            if (
                reality.trackingStatus
                && (reality.trackingStatus !== lastTrackingStatus || (reality.trackingReason ?? '') !== lastTrackingReason)
            ) {
                lastTrackingStatus = reality.trackingStatus;
                lastTrackingReason = reality.trackingReason ?? '';
                arInfo('XR8 tracking status changed', {
                    status: lastTrackingStatus,
                    reason: lastTrackingReason || undefined,
                    frameCount,
                });
                onTrackingStatus?.(reality.trackingStatus, reality.trackingReason);
            }
        },

        onDetach() {
            leave('onDetach');
        },

        onStop() {
            leave('onStop');
        },

        onException(error: Error) {
            arError('XR8 runtime exception', error);
            onException?.(error);
        },

        onCameraStatusChange(args) {
            const status = typeof args?.status === 'string' ? args.status : '';
            arInfo('XR8 camera status', { status });
            if (status) onCameraStatus?.(status);
        },

        listeners: [
            {
                event: 'reality.trackingstatus',
                process: (detail) => {
                    const status = detail.status as string;
                    const reason = detail.reason as string | undefined;
                    arInfo('XR8 tracking status event', { status, reason });
                    onTrackingStatus?.(status, reason);
                },
            },
        ],
    };
}
